/* eslint-disable @typescript-eslint/prefer-enum-initializers */

/**
 * @file
 * @see {@link https://usb.org/sites/default/files/hut1_5.pdf HID Usage Tables}
 * @see {@link https://www.usb.org/sites/default/files/pos1_03.pdf HID Point of Sale Usage Tables}
 */

/**
 * @see {@link https://usb.org/sites/default/files/hut1_5.pdf §33 Scales Page}
 */
export const SCALE_USAGE_PAGE = 0x8d;

/**
 * @see {@link https://usb.org/sites/default/files/hut1_5.pdf §33.3 Reports}
 */
export enum ScaleReport {
  /**
   * Scale Attribute Report
   *
   * Specifies static attributes
   */
  SCALE_ATTRIBUTE_REPORT,
  /**
   * Scale Control Report
   *
   * Controls scale operation, namely to get or set the state of the Enforced Zero Return control
   */
  SCALE_CONTROL_REPORT,
  /**
   * Scale Data Report
   *
   * Transmits current status and weight data. Typically contains the Scale Unit, Data Scaling and Data Weight usages
   */
  SCALE_DATA_REPORT,
  /**
   * Scale Status Report
   *
   * Describes changes to the device status
   */
  SCALE_STATUS_REPORT,
  /**
   * Scale Weight Limit Report
   *
   * Used to identify the maximum weight limit of the scale. This collection typically contains the Scale Unit, Data Scaling and Data Weight usages
   */
  SCALE_WEIGHT_LIMIT_REPORT,
  /**
   * Scale Statistics Report
   *
   * Used to query the current operational statistics of the scale. These statistics include the number of times the scale has been re-zeroed or calibrated.
   */
  SCALE_STATISTICS_REPORT,
}

/**
 * @see {@link https://www.usb.org/sites/default/files/pos1_03.pdf §6.2.4 Scale Data Input Report}
 */
export interface ScaleDataInputReport {
  reportId: ScaleReport.SCALE_DATA_REPORT;
  scaleStatus: ScaleStatus;
  weightUnit: ScaleWeightUnit;
  /**
   * A numeric value representing the scaling applied to the data as a base ten exponent. Range is from 127 to -127
   */
  dataScaling: number;
  /**
   * A binary value representing the weight present on the scale
   */
  dataWeight: number;
}

export function scaleDataInputReportParse(buffer: ArrayBuffer) {
  if (buffer.byteLength !== 6) {
    throw new Error(`Invalid data input report length ${buffer.byteLength}`);
  }
  const view = new DataView(buffer);
  const reportId = view.getUint8(0) as ScaleReport.SCALE_DATA_REPORT;
  const scaleStatus = view.getUint8(1) as ScaleStatus;
  const weightUnit = view.getUint8(2) as ScaleWeightUnit;
  const dataScaling = view.getInt8(3);
  const dataWeight = view.getUint16(4, true);
  return { reportId, scaleStatus, weightUnit, dataScaling, dataWeight };
}

/**
 * @see {@link https://usb.org/sites/default/files/hut1_5.pdf §33.4.1 Weight Units}
 */
export enum ScaleWeightUnit {
  /**
   * Weight Unit Milligrams
   *
   * Data units are in milligrams
   *
   */
  WEIGHT_UNIT_MILLIGRAM = 1,
  /**
   * Weight Unit Grams
   *
   * Data units are in grams
   */
  WEIGHT_UNIT_GRAM = 2,
  /**
   * Weight Unit Kilogram
   *
   * Data units are in kilograms
   */
  WEIGHT_UNIT_KILOGRAM = 3,
  /**
   * Weight Unit Carats
   *
   * Data units are in Carats
   */
  WEIGHT_UNIT_CARATS = 4,
  /**
   * Weight Unit Taels
   *
   * Data units are in Taels
   */
  WEIGHT_UNIT_TAELS = 5,
  /**
   * Weight Unit Grains
   *
   * Data units are in Grains
   */
  WEIGHT_UNIT_GRAINS = 6,
  /**
   * Weight Unit Pennyweights
   *
   * Data units are in Pennyweights
   */
  WEIGHT_UNIT_PENNYWEIGHTS = 7,
  /**
   * Weight Unit Metric Ton
   *
   * Data units are in metric ton
   */
  WEIGHT_UNIT_METRIC_TON = 8,
  /**
   * Weight Unit Avoir Ton
   *
   * Data units are in avoir ton
   */
  WEIGHT_UNIT_AVOIR_TON = 9,
  /**
   * Weight Unit Troy Ounce
   *
   * Data units are in tory ounces
   */
  WEIGHT_UNIT_TROY_OUNCE = 10,
  /**
   * Weight Unit Ounce
   *
   * Data units are in ounces
   */
  WEIGHT_UNIT_OUNCE = 11,
  /**
   * Weight Unit Pound
   *
   * Data units are in pounds
   */
  WEIGHT_UNIT_POUND = 12,
}

/**
 * @see {@link https://usb.org/sites/default/files/hut1_5.pdf §33.6 Scale Status}
 */
export enum ScaleStatus {
  /**
   * Scale Status Fault
   *
   * Some internal scale fault has occurred
   */
  SCALE_STATUS_FAULT = 1,
  /**
   * Scale Status Stable At Center Of Zero
   *
   * The weight is stable and at zero
   */
  SCALE_STATUS_STABLE_AT_CENTER_OF_ZERO = 2,
  /**
   * Scale Status In Motion
   *
   * The scale is not stable; i.e. moving
   */
  SCALE_STATUS_IN_MOTION = 3,
  /**
   * Scale Status Weight Stable
   *
   * Weight on scale is stable and valid
   */
  SCALE_STATUS_WEIGHT_STABLE = 4,
  /**
   * Scale Status Under Zero
   *
   * Weight is stable but is measured at less than zero
   */
  SCALE_STATUS_UNDER_ZERO = 5,
  /**
   * Scale Status Over Weight Limit
   *
   * Weight is stable but exceed the capacity of the scale
   */
  SCALE_STATUS_OVER_WEIGHT_LIMIT = 6,
  /**
   * Scale Status Requires Calibration
   *
   * Scale must be calibrated before data is available
   */
  SCALE_STATUS_REQUIRES_CALIBRATION = 7,
  /**
   * Scale Status Requires Rezeroing
   *
   * Scale must be zeroed before data is available
   */
  SCALE_STATUS_REQUIRES_REZEROING = 8,
}
